export interface BaseProperties {
    arrive_address: string;
    arrive_place_id?: string;
    arrive_station_count: number;
    depart_address: string;
    depart_place_id?: string;
    depart_station_count: number;
    device_form_factor: string;
    funnel_stage: 'search';
    is_one_way: boolean;
    outbound_date: string;
    return_date?: string;
}

export type MapPresentation = 'inline' | 'bottom_sheet';

export type TrackEvent = (name: string, properties?: Record<string, unknown>) => void;

function createEvents(track: TrackEvent) {
    return {
        mapOpened: (base: BaseProperties, uiSource: string, mapPresentation: MapPresentation) => track('Search Interacted', {
            ...base,
            interaction_type: 'map_opened',
            ui_source: uiSource,
            map_presentation: mapPresentation,
        }),

        mapClosed: (base: BaseProperties, uiSource: string, mapPresentation: MapPresentation) => track('Search Interacted', {
            ...base,
            interaction_type: 'map_closed',
            ui_source: uiSource,
            map_presentation: mapPresentation,
        }),

        pinClicked: (base: BaseProperties, station: { id: string; name: string }, direction: string, isDepartingMap: boolean) => track('Station Map Interacted', {
            ...base,
            interaction_type: 'station_selected',
            station_wid: station.id,
            station_name: station.name,
            direction,
            is_departing_map: isDepartingMap,
        }),

        zoomIn: (base: BaseProperties) => track('Station Map Interacted', {
            ...base,
            interaction_type: 'zoom_in',
        }),

        zoomOut: (base: BaseProperties) => track('Station Map Interacted', {
            ...base,
            interaction_type: 'zoom_out',
        }),

        popoverDismissed: (base: BaseProperties) => track('Station Map Interacted', {
            ...base,
            interaction_type: 'popover_dismissed',
        }),

        filterApplied: (base: BaseProperties, stationWid: string, direction: 'departure' | 'arrival', resultsCountBefore: number, isDepartingMap: boolean) => {
            track('Search Filtered', {
                ...base,
                filter_type: `${direction}_stations`,
                ui_source: 'station_map',
                updated_to: stationWid,
                results_count_before: resultsCountBefore,
                is_outbound_results: !base.return_date || isDepartingMap,
            });
        },

        filterRemoved: (base: BaseProperties, stationWid: string, direction: 'departure' | 'arrival', resultsCountBefore: number, isDepartingMap: boolean) => {
            track('Station Map Interacted', {
                ...base,
                interaction_type: 'station_filter_cleared',
                station_wid: stationWid,
                waypoint_type: direction,
                is_departing_map: isDepartingMap,
                is_outbound_results: !base.return_date || isDepartingMap,
            });
            track('Search Filter Removed', {
                ...base,
                filter_type: `${direction}_stations`,
                ui_source: 'station_map',
                updated_from: stationWid,
                results_count_before: resultsCountBefore,
                is_outbound_results: !base.return_date || isDepartingMap,
            });
        },

        chipRemoved: (base: BaseProperties, direction: 'departure' | 'arrival', resultsCountBefore: number) => {
            track('Search Filter Removed', {
                ...base,
                filter_type: `${direction}_stations`,
                ui_source: 'station_map',
                results_count_before: resultsCountBefore,
                is_outbound_results: direction === 'departure',
            });
        },

        allFiltersCleared: (base: BaseProperties, filtersRemoved: string[], resultsCountBefore: number) => {
            track('Station Map Interacted', {
                ...base,
                interaction_type: 'all_filters_cleared',
                filters_removed: filtersRemoved,
            });
            track('Search Filter Removed', {
                ...base,
                filter_type: 'all',
                ui_source: 'station_map',
                filters_removed: filtersRemoved,
                results_count_before: resultsCountBefore,
            });
        },
    };
}

export default createEvents;
